import { theme } from '@nicejob-library/design-system';
import {
    AuthCard,
    AuthContainer,
    AuthContent,
    AuthError,
    AuthFooter,
    AuthLoading,
    OTPInput,
} from '@nicejob-library/design-system/authentication';
import { HiddenInput } from '@nicejob-library/design-system/authentication/forms/Form.styles';
import { Alert, ButtonGroup, Checkbox, Row, Typography } from '@nicejob-library/design-system/core';
import { Countdown } from '@nicejob-library/design-system/core/countdown/Countdown';
import { Overlay } from '@nicejob-library/design-system/core/overlay/Overlay';
import { TooltipContainer } from '@nicejob-library/design-system/core/tooltip/styles';
import { translate } from '@nicejob-library/internationalization';
import { Paths } from '@nicejob-library/paths';
import axios from 'axios';
import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { ThemeProvider } from 'styled-components';
import { CDN_DOMAIN } from '../../../../../../common';
import {
    CONTACT_SUPPORT_EMAIL,
    CONTACT_SUPPORT_PHONE_NUMBER,
    OTP_MAX_LENGTH,
} from '../../../../../../common/globals/globals';
import { TEST_ID } from '../../../../../../common/testing/constants';
import { IVerifyPageProps } from '../../../../types/login.types';
import { HeaderLogo } from '../../components/common/HeaderLogo';
import { LoginLink } from '../../components/common/LoginLink';
import { SubmitInput } from './index.styles';

export function VerifyPage(props: IVerifyPageProps): ReactElement {
    return (
        <ThemeProvider theme={theme}>
            <VerifyPageContent {...props} />
        </ThemeProvider>
    );
}

function VerifyPageContent(props: IVerifyPageProps): ReactElement {
    const {
        language,
        branding,
        error_message,
        email,
        password,
        phone_number,
        post_url,
        verification_id: initial_verification_id,
    } = props;
    const [is_loading, setIsLoading] = useState(false);
    const [verification_id, setVerificationId] = useState(initial_verification_id);
    const input_refs = useRef<Array<HTMLInputElement | null>>([]);
    const [otp, setOTP] = useState('');
    const [show_tooltip, setShowTooltip] = useState(false);
    const [remember_device, setRememberDevice] = useState(false);
    const [cooldown_seconds, setCooldownSeconds] = useState(0);
    const [resend_error, setResendError] = useState<string | null>(null);
    const help_link_ref = useRef(null);
    const cooldown_timer_ref = useRef<NodeJS.Timeout | null>(null);
    const resend_debounce_ref = useRef<NodeJS.Timeout | null>(null);

    function handleSubmit(e: React.FormEvent): void {
        e.preventDefault();
        setIsLoading(true);

        const form = e.target as HTMLFormElement;
        const inputs = form.querySelectorAll('input');
        inputs.forEach(input => {
            const input_element = input as HTMLInputElement;
            if (input_element.name === 'email') {
                input_element.value = email;
            } else if (input_element.name === 'password') {
                input_element.value = password;
            } else if (input_element.name === 'otp') {
                input_element.value = otp;
            } else if (input_element.name === 'verification_id') {
                input_element.value = verification_id;
            } else if (input_element.name === 'remember_device') {
                input_element.value = remember_device ? '1' : '0';
            }
        });

        form.submit();
    }

    function handleResendCode(): void {
        // Throttle: ignore clicks if already in progress or within throttle period
        if (resend_debounce_ref.current) {
            return; // Ignore subsequent clicks
        }

        // Execute immediately on first click
        setResendError(null);

        axios
            .post(
                Paths.VERIFICATION_RESEND,
                {
                    email, //TBD, do we need to change this into user_id?
                },
                {
                    withCredentials: true,
                }
            )
            .then(response => {
                if (response.data.success && response.data.verification_id) {
                    setVerificationId(response.data.verification_id);
                    // Start countdown if cooldown_seconds is provided
                    if (response.data.cooldown_seconds) {
                        startCooldownTimer(response.data.cooldown_seconds);
                    }
                } else if (!response.data.success && response.data.error) {
                    setResendError(response.data.error);
                    // If there's a cooldown, start the timer
                    if (response.data.cooldown_seconds) {
                        startCooldownTimer(response.data.cooldown_seconds);
                    }
                }
            })
            .catch(error => {
                if (error.response?.data?.error) {
                    setResendError(error.response.data.error);
                    if (error.response.data.cooldown_seconds) {
                        startCooldownTimer(error.response.data.cooldown_seconds);
                    }
                } else {
                    setResendError(
                        translate({
                            namespace: 'login',
                            key: 'verify.resend_error',
                            language,
                        })
                    );
                }
            })
            .finally(() => {
                // Set throttle timer to prevent rapid clicks (3 seconds)
                resend_debounce_ref.current = setTimeout(() => {
                    resend_debounce_ref.current = null;
                }, 3000);
            });
    }

    function startCooldownTimer(seconds: number): void {
        // Clear any existing timer
        if (cooldown_timer_ref.current) {
            clearInterval(cooldown_timer_ref.current);
        }

        setCooldownSeconds(seconds);

        cooldown_timer_ref.current = setInterval(() => {
            setCooldownSeconds(prev => {
                if (prev <= 1) {
                    // Cooldown complete
                    if (cooldown_timer_ref.current) {
                        clearInterval(cooldown_timer_ref.current);
                    }

                    return 0;
                }

                return prev - 1;
            });
        }, 1000);
    }

    // Clean up timers on component unmount
    useEffect(() => {
        return (): void => {
            if (cooldown_timer_ref.current) {
                clearInterval(cooldown_timer_ref.current);
            }
            if (resend_debounce_ref.current) {
                clearTimeout(resend_debounce_ref.current);
            }
        };
    }, []);

    useEffect(() => {
        const inputs = input_refs.current;

        const handleFocus = (event: FocusEvent): void => {
            const target = event.target as HTMLInputElement;
            target.select();
        };

        inputs.forEach(input => {
            if (input) {
                input.addEventListener('focus', handleFocus);
                input.addEventListener('click', handleFocus);
            }
        });

        return (): void => {
            inputs.forEach(input => {
                if (input) {
                    input.removeEventListener('focus', handleFocus);
                    input.removeEventListener('click', handleFocus);
                }
            });
        };
    }, []);

    return (
        <AuthContainer>
            <HeaderLogo branding={branding} />

            {is_loading && <AuthLoading />}

            <AuthContent is_loading={is_loading}>
                <AuthCard>
                    <form onSubmit={handleSubmit} action={post_url} method='POST'>
                        <Row sx={{ mt: '29px', justifyContent: 'center' }}>
                            <img src={`${CDN_DOMAIN}/assets/secure.svg`} alt='Secure' />
                        </Row>
                        <Row sx={{ p: '37px 30px', justifyContent: 'center' }}>
                            <h1
                                style={{
                                    fontSize: '24px',
                                    fontWeight: 600,
                                    margin: 0,
                                    color: '#333',
                                }}
                            >
                                {translate({
                                    namespace: 'login',
                                    key: 'verify.header',
                                    language,
                                })}
                            </h1>
                        </Row>
                        <Row sx={{ mb: '16px' }}>
                            <h3
                                style={{
                                    fontSize: '16px',
                                    fontWeight: 400,
                                    margin: 0,
                                    color: '#17191C',
                                }}
                            >
                                {phone_number
                                    ? translate({
                                          namespace: 'login',
                                          key: 'verify.enter_code_from_sms',
                                          language,
                                          params: { phone_number },
                                      })
                                    : translate({
                                          namespace: 'login',
                                          key: 'verify.enter_code_from_email',
                                          language,
                                          params: { email },
                                      })}
                            </h3>
                        </Row>
                        <OTPInput
                            value={otp}
                            value_length={OTP_MAX_LENGTH}
                            onChange={(value: string): void => {
                                setOTP(value);
                            }}
                        />
                        <Row sx={{ mt: 30, mb: 12 }}>
                            <Checkbox
                                labelStyle={{
                                    fontWeight: 400,
                                    color: '#5E646E',
                                    cursor: 'pointer',
                                }}
                                label={translate({
                                    namespace: 'login',
                                    key: 'verify.remember_device',
                                    language,
                                })}
                                value='1'
                                selected_checkboxes={remember_device ? ['1'] : []}
                                onChange={(_value, action): void => {
                                    if (action === 'ADD_ITEM') {
                                        setRememberDevice(true);
                                    } else {
                                        setRememberDevice(false);
                                    }
                                }}
                            />
                        </Row>

                        <HiddenInput type='text' name='email' value='' />
                        <HiddenInput type='text' name='password' value='' />
                        <HiddenInput type='text' name='otp' value='' />
                        <HiddenInput type='text' name='verification_id' value='' />
                        <HiddenInput
                            type='text'
                            name='remember_device'
                            value={remember_device ? '1' : '0'}
                        />

                        <ButtonGroup position='right'>
                            <SubmitInput
                                disabled={otp.length !== OTP_MAX_LENGTH}
                                name='login'
                                type='submit'
                                data-testid={TEST_ID.login.submit_button}
                                value={translate({
                                    namespace: 'login',
                                    key: 'verify.submit_button',
                                    language,
                                })}
                            />
                        </ButtonGroup>

                        <Row sx={{ mt: '10px' }}>
                            <Alert
                                show_alert={!!resend_error}
                                message={resend_error || ''}
                                variant='serious'
                                onClose={(): void => setResendError(null)}
                            />
                        </Row>

                        <Row
                            sx={{ mb: '10px' }}
                            style={{
                                marginTop: '37px',
                                display: 'flex',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Countdown
                                time_span_in_seconds={cooldown_seconds}
                                loading_message={translate({
                                    namespace: 'login',
                                    key: 'verify.link.resend_code_in',
                                    language,
                                })}
                            >
                                <Typography.Link
                                    color={theme.colors.blue600}
                                    undecorated={true}
                                    onClick={handleResendCode}
                                >
                                    {translate({
                                        namespace: 'login',
                                        key: 'verify.link.resend_code',
                                        language,
                                    })}
                                </Typography.Link>
                            </Countdown>

                            <span ref={help_link_ref}>
                                <Typography.Link
                                    onClick={(): void => {
                                        setShowTooltip(true);
                                    }}
                                    color={theme.colors.blue600}
                                    undecorated={true}
                                >
                                    {translate({
                                        namespace: 'login',
                                        key: 'verify.link.need_help',
                                        language,
                                    })}
                                </Typography.Link>
                            </span>

                            <Overlay
                                trigger_ref={help_link_ref}
                                visible={show_tooltip}
                                trigger='hover'
                                is_barebones
                                overlay_position='bottom'
                                offset={[0, 8]}
                                onOutsideClick={(): void => {
                                    setShowTooltip(false);
                                }}
                            >
                                <TooltipContainer style={{ padding: '12px 16px' }}>
                                    <Row style={{ color: theme.colors.white, fontSize: '14px' }}>
                                        {translate({
                                            namespace: 'login',
                                            key: 'verify.contact_support',
                                            language,
                                        })}
                                    </Row>
                                    <Row
                                        style={{
                                            color: theme.colors.white,
                                            fontSize: '14px',
                                            marginTop: '8px',
                                        }}
                                    >
                                        <span
                                            role='img'
                                            aria-label='Phone'
                                            style={{ marginRight: '4px' }}
                                        >
                                            📞
                                        </span>
                                        {CONTACT_SUPPORT_PHONE_NUMBER}
                                    </Row>
                                    <Row style={{ color: theme.colors.white, fontSize: '14px' }}>
                                        <span
                                            role='img'
                                            aria-label='Email'
                                            style={{ marginRight: '4px' }}
                                        >
                                            ✉️
                                        </span>
                                        {CONTACT_SUPPORT_EMAIL}
                                    </Row>
                                    <Row
                                        style={{
                                            color: theme.colors.white,
                                            fontSize: '14px',
                                            marginTop: '8px',
                                            textAlign: 'left',
                                        }}
                                    >
                                        {translate({
                                            namespace: 'login',
                                            key: 'verify.time1',
                                            language,
                                        })}
                                        <br />
                                        {translate({
                                            namespace: 'login',
                                            key: 'verify.time2',
                                            language,
                                        })}
                                    </Row>
                                </TooltipContainer>
                            </Overlay>
                        </Row>
                    </form>
                    <AuthError error={error_message} error_prefix='Error' />
                </AuthCard>
                <AuthFooter>
                    <LoginLink branding={branding} language={language} />
                </AuthFooter>
            </AuthContent>
        </AuthContainer>
    );
}
